import React, { useState, useEffect } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import {
  useGetTransactionsQuery,
  EbillsTransaction,
} from "../../../features/ebills/ebillsSlice";
import { Loader } from "../../../shared/Loader";
import { formatDateWithTimeAndDate } from "../../../utils/formatDate";

export const BillsTransactionHistory = () => {
  const [page, setPage] = useState<number>(1);
  const [searchParams] = useSearchParams();
  const pageFromUrl = searchParams.get("page");

  useEffect(() => {
    if (pageFromUrl) {
      setPage(Number(pageFromUrl));
    } else {
      setPage(1);
    }
  }, [pageFromUrl]);

  const {
    data: transactionsData,
    isLoading,
    isFetching,
  } = useGetTransactionsQuery({
    page: Number(pageFromUrl) || page,
    limit: 20,
  });

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === "completed") {
      return (
        <div className="badge-success px-4 py-2 rounded-full text-xs">
          Completed
        </div>
      );
    } else if (statusLower === "failed") {
      return (
        <div className="badge-error px-4 py-2 rounded-full text-xs">
          Failed
        </div>
      );
    } else if (statusLower === "pending") {
      return (
        <div className="badge-warning px-4 py-2 rounded-full text-xs">
          Pending
        </div>
      );
    } else {
      return (
        <div className="badge-info px-4 py-2 rounded-full text-xs">
          {status}
        </div>
      );
    }
  };

  const totalPages = transactionsData
    ? Math.ceil(transactionsData.total / transactionsData.limit)
    : 1;
  const currentPage = Number(pageFromUrl) || page;

  return (
    <div className="bg-black flex flex-col gap-4 px-4 lg:px-10 py-10 mt-8">
      <h3 className="text-primary text-xl font-medium">Transaction History</h3>

      <div className="overflow-x-auto relative">
        {isLoading || isFetching ? (
          <div className="w-full h-[65vh] flex justify-center items-center">
            <Loader />
          </div>
        ) : transactionsData?.transactions?.length === 0 ? (
          <div className="w-full bg-red font-light text-white/40 my-24 text-center">
            No transactions found
          </div>
        ) : (
          <table className="relative w-full text-sm text-left text-gray-400">
            <thead className="text-xs uppercase bg-gray-700 text-gray-300">
              <tr>
                <th scope="col" className="py-3 px-6">
                  Request ID
                </th>
                <th scope="col" className="py-3 px-6">
                  Product
                </th>
                <th scope="col" className="py-3 px-6">
                  Service
                </th>
                <th scope="col" className="py-3 px-6">
                  Amount
                </th>
                <th scope="col" className="py-3 px-6">
                  Fee
                </th>
                <th scope="col" className="py-3 px-6">
                  Total
                </th>
                <th scope="col" className="py-3 px-6">
                  Status
                </th>
                <th scope="col" className="py-3 px-6">
                  Created At
                </th>
                <th scope="col" className="py-3 px-6"></th>
              </tr>
            </thead>
            <tbody>
              {transactionsData?.transactions?.map(
                (transaction: EbillsTransaction) => (
                  <tr
                    key={transaction.request_id}
                    className="border-b bg-transparent border-gray-700"
                  >
                    <th
                      scope="row"
                      className="py-4 px-6 font-medium whitespace-nowrap text-white"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{transaction.request_id}</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(
                              transaction.request_id
                            );
                          }}
                          className="text-white/60 hover:text-white"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                      </div>
                    </th>
                    <td className="py-4 px-6 capitalize">
                      {transaction.product}
                    </td>
                    <td className="py-4 px-6 capitalize">
                      {transaction.service_id || "N/A"}
                    </td>
                    <td className="py-4 px-6">
                      ₦{transaction.amount.toLocaleString()}
                    </td>
                    <td className="py-4 px-6">
                      ₦{transaction.fee.toLocaleString()}
                    </td>
                    <td className="py-4 px-6">
                      ₦{transaction.total.toLocaleString()}
                    </td>
                    <td className="py-4 px-6">
                      {getStatusBadge(transaction.status)}
                    </td>
                    <td className="py-4 px-6">
                      {formatDateWithTimeAndDate(transaction.created_at)}
                    </td>
                    <td className="py-4 px-6">
                      <Link
                        to={`/app/bills/transactions/${transaction.request_id}`}
                      >
                        <button className="box-content text-blue-500 text-xs">
                          Details
                        </button>
                      </Link>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        )}

        <div className="flex items-center gap-5 mt-12 ml-6">
          <p className="text-white/60 font-light text-sm">
            page: {currentPage} | total: {transactionsData?.total || 0}{" "}
            transactions
          </p>
          <div className="flex items-center gap-2">
            <Link to={`/app/bills?page=${currentPage - 1 || 1}`}>
              <button
                className="rounded-full bg-gray-500 hover:bg-gray-600 text-white/70 disabled:cursor-not-allowed disabled:bg-gray-500/50 disabled:text-white/20 text-lg cursor-pointer"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
              >
                <FiChevronLeft className="box-content p-3" />
              </button>
            </Link>
            <Link
              to={`/app/bills?page=${
                currentPage < totalPages ? currentPage + 1 : currentPage
              }`}
            >
              <button
                className="rounded-full bg-gray-500 hover:bg-gray-600 text-white/70 disabled:cursor-not-allowed disabled:bg-gray-500/50 disabled:text-white/20 text-lg cursor-pointer"
                disabled={currentPage >= totalPages}
                onClick={() => setPage(currentPage + 1)}
              >
                <FiChevronRight className="box-content p-3" />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

